import { useState } from "react";
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    ArrowRight,
    Flame,
    Thermometer,
    Grid3x3,
    LayoutGrid,
    Wind,
    Zap,
    Bath,
    ShieldCheck,
    Award,
    Wrench,
    CheckCircle2,
    Sparkles,
    X,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BUSINESS, SERVICES, GALLERY } from "@/lib/business";

const ICONS = { Flame, Thermometer, Grid3x3, LayoutGrid, Wind, Zap, Bath };

export default function Home() {
    const [lightbox, setLightbox] = useState(null);

    return (
        <div data-testid="home-page" className="bg-brand-cream overflow-x-hidden">
            <Header />

            {/* HERO */}
            <section
                id="inicio"
                data-testid="hero-section"
                className="relative pt-[72px] min-h-[92vh] flex items-end bg-brand-navy text-brand-cream overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img
                        src="/assets/gallery/work-11.jpg"
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover opacity-[0.35]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/90 to-brand-navy/40" />
                    <div className="bg-grid-navy absolute inset-0 opacity-70 pointer-events-none" />
                </div>

                <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 lg:px-12 py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="reveal reveal-1 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/8 ring-1 ring-white/15 backdrop-blur-sm text-[12px] uppercase tracking-[0.2em] font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-red" />
                            Puente la Reina · Navarra
                        </div>
                        <h1 className="reveal reveal-2 font-display font-bold mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            Fontanería y
                            <br />
                            <span className="relative inline-block">
                                <span className="relative z-10 text-brand-red">calefacción</span>
                                <span className="absolute -bottom-2 left-0 right-0 h-2 bg-brand-red/20 rounded-full blur-md" />
                            </span>{" "}
                            de confianza.
                        </h1>
                        <p className="reveal reveal-3 mt-7 text-lg sm:text-xl text-brand-cream/75 max-w-2xl leading-relaxed">
                            Calderas, aerotermia, suelo radiante, radiadores, gas y baños completos. Trabajo artesanal, limpio y garantizado en toda Navarra.
                        </p>
                        <div className="reveal reveal-4 mt-9 flex flex-wrap items-center gap-3">
                            <a
                                href={BUSINESS.phoneHref}
                                data-testid="hero-call-cta"
                                className="pill group inline-flex items-center gap-2.5 bg-brand-red hover:bg-brand-red-600 text-white px-7 py-3.5 font-semibold text-[15px] transition-all shadow-[0_16px_40px_-16px_hsl(var(--brand-red)/0.85)]"
                            >
                                <Phone className="h-4 w-4" />
                                Llamar {BUSINESS.phone}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </a>
                            <a
                                href="#servicios"
                                data-testid="hero-services-cta"
                                className="pill inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm ring-1 ring-white/15 text-white px-6 py-3.5 font-semibold text-[15px] transition-all"
                            >
                                Ver servicios
                            </a>
                        </div>

                        <div className="reveal reveal-4 mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
                            {[
                                { k: "+20", l: "Años de oficio" },
                                { k: "7", l: "Especialidades" },
                                { k: "100%", l: "Trabajo propio" },
                                { k: "Navarra", l: "Zona de trabajo" },
                            ].map((s, i) => (
                                <div key={i} className="border-l border-white/15 pl-4">
                                    <div className="font-display font-bold text-2xl sm:text-3xl text-white">{s.k}</div>
                                    <div className="mt-1 text-[12px] uppercase tracking-[0.14em] text-brand-cream/60 font-semibold">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST MARQUEE */}
            <section aria-hidden="true" className="bg-brand-red text-white py-4 overflow-hidden">
                <div className="flex whitespace-nowrap animate-marquee">
                    {[...Array(2)].map((_, r) => (
                        <div key={r} className="flex shrink-0 items-center gap-12 px-6 text-[14px] font-semibold uppercase tracking-[0.18em]">
                            {[
                                "Calderas",
                                "Aerotermia",
                                "Suelo radiante",
                                "Radiadores",
                                "Baños completos",
                                "Instalaciones de gas",
                                "Calefacción",
                                "Fontanería",
                            ].map((t, i) => (
                                <span key={`${r}-${i}`} className="flex items-center gap-12">
                                    <span>{t}</span>
                                    <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICIOS */}
            <section id="servicios" data-testid="services-section" className="py-24 md:py-32 relative">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-red">
                                <span className="h-px w-8 bg-brand-red" /> Nuestros servicios
                            </div>
                            <h2 className="font-display font-bold mt-4 text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-[1.05]">
                                Todo lo que necesita tu hogar,
                                <span className="text-brand-blue-700"> bajo un mismo techo.</span>
                            </h2>
                        </div>
                        <p className="text-brand-ink/70 max-w-md md:text-right text-[15px] leading-relaxed">
                            Más de dos décadas instalando y reparando sistemas de agua, gas y calefacción con la misma obsesión: que todo funcione bien y dure muchos años.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICES.map((s, idx) => {
                            const Icon = ICONS[s.icon] || Wrench;
                            const featured = idx === 0;
                            return (
                                <article
                                    key={s.id}
                                    data-testid={`service-${s.id}`}
                                    className={`group relative rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1 ${
                                        featured
                                            ? "sm:col-span-2 lg:col-span-2 bg-brand-navy text-brand-cream ring-1 ring-black/5 shadow-[0_30px_60px_-30px_rgba(11,36,71,0.5)]"
                                            : "bg-white ring-1 ring-black/5 hover:ring-brand-red/40 shadow-[0_6px_20px_-10px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.2)]"
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div
                                            className={`h-12 w-12 rounded-2xl grid place-items-center ${
                                                featured
                                                    ? "bg-brand-red text-white"
                                                    : "bg-brand-red/10 text-brand-red group-hover:bg-brand-red group-hover:text-white"
                                            } transition-colors`}
                                        >
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <span className={`text-[11px] font-mono ${featured ? "text-brand-cream/50" : "text-brand-ink/40"}`}>
                                            0{idx + 1}
                                        </span>
                                    </div>
                                    <h3 className={`mt-6 font-display font-bold text-2xl tracking-tight ${featured ? "text-white" : "text-brand-navy"}`}>
                                        {s.title}
                                    </h3>
                                    <p className={`mt-2 text-[13px] uppercase tracking-[0.14em] font-semibold ${featured ? "text-brand-red" : "text-brand-blue-700"}`}>
                                        {s.tagline}
                                    </p>
                                    <p className={`mt-4 text-[15px] leading-relaxed ${featured ? "text-brand-cream/75" : "text-brand-ink/70"}`}>
                                        {s.description}
                                    </p>
                                    <ul className="mt-5 space-y-2">
                                        {s.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className={`flex items-start gap-2 text-[14px] ${featured ? "text-brand-cream/80" : "text-brand-ink/75"}`}
                                            >
                                                <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-brand-red" : "text-brand-blue"}`} />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SOBRE NOSOTROS */}
            <section id="nosotros" data-testid="about-section" className="py-24 md:py-32 bg-white relative">
                <div className="bg-grain absolute inset-0 opacity-40 pointer-events-none" />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <div className="relative">
                                <div className="rounded-3xl overflow-hidden ring-1 ring-black/5 aspect-[5/4]">
                                    <img
                                        src="/assets/gallery/work-26.jpg"
                                        alt="Instalación realizada por Fontanería Rodrigo"
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                                <div className="absolute -bottom-8 -right-4 md:-right-10 hidden sm:block rounded-3xl overflow-hidden ring-1 ring-black/5 w-48 md:w-64 aspect-[4/5] shadow-xl">
                                    <img
                                        src="/assets/gallery/work-4.jpg"
                                        alt="Trabajo de fontanería"
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                                <div className="absolute -top-6 -left-6 rounded-2xl bg-brand-red text-white p-5 shadow-xl max-w-[200px]">
                                    <div className="font-display font-bold text-3xl leading-none">+20</div>
                                    <div className="mt-1 text-[12px] uppercase tracking-[0.14em] font-semibold text-white/85">
                                        Años en Navarra
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 order-1 lg:order-2">
                            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-red">
                                <span className="h-px w-8 bg-brand-red" /> Sobre nosotros
                            </div>
                            <h2 className="font-display font-bold mt-4 text-4xl sm:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                Oficio, cercanía y trabajo bien hecho.
                            </h2>
                            <p className="mt-6 text-[16px] leading-relaxed text-brand-ink/75">
                                En <strong className="text-brand-navy">Fontanería Rodrigo S.L.</strong> somos una empresa familiar con sede en Puente la Reina. Llevamos más de dos décadas instalando calderas, radiadores y suelo radiante en casas y locales de toda Navarra.
                            </p>
                            <p className="mt-4 text-[16px] leading-relaxed text-brand-ink/75">
                                Trabajamos con primeras marcas, damos presupuesto claro y nos comprometemos con el plazo. Y cuando terminamos, dejamos la obra como la encontramos.
                            </p>

                            <div className="mt-8 grid grid-cols-2 gap-5">
                                {[
                                    { icon: ShieldCheck, t: "Garantía", d: "En cada instalación realizada" },
                                    { icon: Award, t: "Experiencia", d: "Más de 20 años en el sector" },
                                    { icon: Wrench, t: "Trabajo propio", d: "Sin subcontratas intermedias" },
                                    { icon: Sparkles, t: "Limpieza", d: "Cuidamos tu hogar" },
                                ].map((f, i) => (
                                    <div key={i} className="rounded-2xl bg-brand-cream p-4 ring-1 ring-black/5">
                                        <f.icon className="h-5 w-5 text-brand-red" />
                                        <div className="mt-3 font-display font-semibold text-brand-navy text-[15px]">{f.t}</div>
                                        <div className="text-[13px] text-brand-ink/65 mt-0.5">{f.d}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* POR QUÉ ELEGIRNOS */}
            <section data-testid="why-section" className="py-24 bg-brand-navy text-brand-cream relative overflow-hidden">
                <div className="bg-grid-navy absolute inset-0 opacity-40 pointer-events-none" />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-red">
                            <span className="h-px w-8 bg-brand-red" /> ¿Por qué Rodrigo?
                        </div>
                        <h2 className="font-display font-bold mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            La diferencia está en los detalles.
                        </h2>
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                n: "01",
                                t: "Asesoramiento honesto",
                                d: "Estudiamos tu caso y proponemos lo que realmente necesitas, sin venderte de más.",
                            },
                            {
                                n: "02",
                                t: "Presupuesto cerrado",
                                d: "Sin sorpresas ni extras inesperados. Lo que firmamos es lo que pagas.",
                            },
                            {
                                n: "03",
                                t: "Instaladores propios",
                                d: "El equipo que te atiende es el mismo que hace la obra, con años de experiencia.",
                            },
                            {
                                n: "04",
                                t: "Servicio postventa",
                                d: "Seguimos disponibles cuando terminamos. Una instalación bien hecha se nota con el tiempo.",
                            },
                        ].map((p, i) => (
                            <div
                                key={i}
                                className="group relative rounded-2xl p-7 ring-1 ring-white/10 bg-white/5 hover:bg-white/8 transition-colors"
                            >
                                <div className="font-mono text-brand-red text-sm">{p.n}</div>
                                <h3 className="mt-4 font-display font-bold text-xl text-white">{p.t}</h3>
                                <p className="mt-2 text-[14px] leading-relaxed text-brand-cream/70">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GALERIA */}
            <section id="galeria" data-testid="gallery-section" className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-red">
                                <span className="h-px w-8 bg-brand-red" /> Nuestros trabajos
                            </div>
                            <h2 className="font-display font-bold mt-4 text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-[1.05]">
                                Fotos reales de instalaciones propias.
                            </h2>
                        </div>
                        <p className="max-w-sm text-brand-ink/70 md:text-right text-[15px]">
                            Todas las imágenes corresponden a proyectos realizados por nuestro equipo en hogares y locales de Navarra.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                        {GALLERY.map((img, i) => {
                            const spanBig = i === 0 || i === 7 || i === 12;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    data-testid={`gallery-item-${i}`}
                                    onClick={() => setLightbox(img)}
                                    className={`gallery-item rounded-2xl ring-1 ring-black/5 bg-brand-stone cursor-zoom-in relative ${
                                        spanBig ? "row-span-2 aspect-[3/4] sm:aspect-auto" : "aspect-square"
                                    }`}
                                >
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {lightbox && (
                    <div
                        data-testid="lightbox"
                        onClick={() => setLightbox(null)}
                        className="fixed inset-0 z-[70] bg-brand-navy/95 backdrop-blur-sm grid place-items-center p-4 animate-in"
                        style={{ animation: "reveal 0.3s ease both" }}
                    >
                        <button
                            onClick={() => setLightbox(null)}
                            className="absolute top-6 right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
                            aria-label="Cerrar"
                            data-testid="lightbox-close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <img
                            src={lightbox.src}
                            alt={lightbox.alt}
                            className="max-h-[85vh] max-w-[92vw] rounded-xl shadow-2xl object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}
            </section>

            {/* CONTACTO */}
            <section id="contacto" data-testid="contact-section" className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                        <div className="lg:col-span-5">
                            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-red">
                                <span className="h-px w-8 bg-brand-red" /> Contacto
                            </div>
                            <h2 className="font-display font-bold mt-4 text-4xl sm:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                ¿Hablamos de tu proyecto?
                            </h2>
                            <p className="mt-5 text-[16px] leading-relaxed text-brand-ink/75 max-w-md">
                                Cuéntanos lo que necesitas y te damos presupuesto sin compromiso. Llámanos o escríbenos, lo que te resulte más cómodo.
                            </p>

                            <div className="mt-10 space-y-4">
                                <a
                                    href={BUSINESS.phoneHref}
                                    data-testid="contact-phone"
                                    className="group flex items-center gap-5 p-5 rounded-2xl bg-brand-cream ring-1 ring-black/5 hover:ring-brand-red/40 hover:bg-white transition-all"
                                >
                                    <div className="h-12 w-12 rounded-xl bg-brand-red text-white grid place-items-center">
                                        <Phone className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-brand-ink/55">Teléfono</div>
                                        <div className="font-display font-bold text-xl text-brand-navy group-hover:text-brand-red transition-colors">
                                            {BUSINESS.phone}
                                        </div>
                                    </div>
                                </a>

                                <a
                                    href={BUSINESS.emailHref}
                                    data-testid="contact-email"
                                    className="group flex items-center gap-5 p-5 rounded-2xl bg-brand-cream ring-1 ring-black/5 hover:ring-brand-red/40 hover:bg-white transition-all"
                                >
                                    <div className="h-12 w-12 rounded-xl bg-brand-blue-700 text-white grid place-items-center">
                                        <Mail className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-brand-ink/55">Email</div>
                                        <div className="font-display font-bold text-lg text-brand-navy group-hover:text-brand-blue-700 transition-colors break-all">
                                            {BUSINESS.email}
                                        </div>
                                    </div>
                                </a>

                                <div className="flex items-start gap-5 p-5 rounded-2xl bg-brand-cream ring-1 ring-black/5">
                                    <div className="h-12 w-12 rounded-xl bg-brand-navy text-white grid place-items-center shrink-0">
                                        <MapPin className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-brand-ink/55">Dirección</div>
                                        <div className="font-display font-semibold text-brand-navy text-[15px] leading-tight mt-1">
                                            {BUSINESS.address}
                                        </div>
                                        <div className="text-[14px] text-brand-ink/70 mt-0.5">
                                            {BUSINESS.postalCode} {BUSINESS.city}, {BUSINESS.province}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5 p-5 rounded-2xl bg-brand-cream ring-1 ring-black/5">
                                    <div className="h-12 w-12 rounded-xl bg-brand-navy text-white grid place-items-center shrink-0">
                                        <Clock className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-brand-ink/55">Horario</div>
                                        <div className="font-display font-semibold text-brand-navy text-[15px] mt-1">
                                            {BUSINESS.hours}
                                        </div>
                                        <div className="text-[13px] text-brand-ink/60 mt-0.5">
                                            Sábados, domingos y festivos cerrado
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-xl h-full min-h-[520px] bg-brand-stone">
                                <iframe
                                    title="Ubicación Fontanería Rodrigo"
                                    data-testid="contact-map"
                                    src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapsQuery)}&output=embed`}
                                    className="w-full h-full min-h-[520px] border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
